/**
 * User List Page Component
 *
 * Displays a paginated table of all users in the system with filtering capabilities.
 *
 * Features:
 * - Displays 20 users per page with pagination controls
 * - Shows telegram_id, username, balance, consent_marketing, and created_at
 * - Handles loading states and empty states
 * - Displays friendly error messages on API failures
 * - Row click to view user details (navigates to detail page)
 *
 * Requirements:
 * - 3.1-3.9: User list with pagination and filtering
 * - Uses Refine useTable hook for data management
 * - Uses Ant Design Table component for display
 */

import React, { useState, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import {
  Table,
  Typography,
  Tag,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Tabs,
  Space,
  Button,
  Spin,
  Empty,
  Card
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useList, useDataProvider } from "@refinedev/core";
import dayjs from "dayjs";
import type { UserResponse, TransactionResponse } from "../../api-client";
import { AddBonusModal } from "./components";

const { Text } = Typography;
const { TabPane } = Tabs;

/**
 * User list page component.
 *
 * Displays a table of users with pagination, loading states, and error handling.
 * Each row is clickable and navigates to the user detail page.
 */
export const UserList: React.FC = () => {
  // State for drawer
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isBonusModalVisible, setIsBonusModalVisible] = useState(false);

  // useTable hook manages data fetching, pagination, and loading states
  const { tableProps } = useTable<UserResponse>({
    resource: "users",
    pagination: {
      pageSize: 20,
      mode: "server", // Server-side pagination
    },
    syncWithLocation: true, // Sync pagination with URL
  });

  // Manual user data fetching using dataProvider directly
  const dataProvider = useDataProvider();
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [userLoading, setUserLoading] = useState(false);

  // Fetch user data when selectedUserId changes
  useEffect(() => {
    if (!selectedUserId) {
      setSelectedUser(null);
      return;
    }

    setUserLoading(true);

    dataProvider()
      .getOne({
        resource: "users",
        id: selectedUserId,
      })
      .then((result) => {
        setSelectedUser(result.data as UserResponse);
        setUserLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch user:', error);
        setSelectedUser(null);
        setUserLoading(false);
      });
  }, [selectedUserId]);

  const refetchUser = () => {
    if (selectedUserId) {
      setUserLoading(true);
      dataProvider()
        .getOne({
          resource: "users",
          id: selectedUserId,
        })
        .then((result) => {
          setSelectedUser(result.data as UserResponse);
          setUserLoading(false);
        })
        .catch((error) => {
          console.error('Error refetching user:', error);
          setUserLoading(false);
        });
    }
  };

  // Fetch transactions for selected user
  const transactionsQuery = useList<TransactionResponse>({
    resource: "transactions",
    pagination: {
      pageSize: 20,
    },
    meta: {
      user_id: selectedUserId,
    },
    queryOptions: {
      enabled: !!selectedUserId,
    },
  });
  const transactionsData = transactionsQuery.result?.data || [];
  const transactionsLoading = transactionsQuery.query.isLoading || false;
  const refetchTransactions = transactionsQuery.query.refetch;

  /**
   * Table column definitions with custom rendering for each field.
   */
  const columns: ColumnsType<UserResponse> = [
    {
      title: "Telegram ID",
      dataIndex: "telegram_id",
      key: "telegram_id",
      width: 150,
      sorter: false,
      render: (value: number) => (
        <Text strong>{value}</Text>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 200,
      sorter: false,
      render: (value: string | null | undefined) => {
        if (!value) {
          return <Text type="secondary">—</Text>;
        }
        return <Text>@{value}</Text>;
      },
    },
    {
      title: "Баланс",
      dataIndex: "balance",
      key: "balance",
      width: 120,
      sorter: false,
      align: "right",
      render: (value: number) => (
        <Text strong style={{ color: value > 0 ? "#52c41a" : undefined }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Маркетинг",
      dataIndex: "consent_marketing",
      key: "consent_marketing",
      width: 120,
      sorter: false,
      align: "center",
      render: (value: boolean) => {
        if (value) {
          return (
            <Tooltip title="Согласие получено">
              <Tag icon={<CheckCircleOutlined />} color="success">
                Да
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Согласие не получено">
            <Tag icon={<CloseCircleOutlined />} color="default">
              Нет
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Дата регистрации",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      sorter: false,
      render: (value: string) => {
        const date = dayjs(value);
        return (
          <Tooltip title={date.format("DD.MM.YYYY HH:mm:ss")}>
            <Text>{date.format("DD.MM.YYYY")}</Text>
          </Tooltip>
        );
      },
    },
  ];

  /**
   * Handle row click to open user detail drawer.
   */
  const handleRowClick = (record: UserResponse) => {
    setSelectedUserId(record.telegram_id);
    setIsDrawerVisible(true);
  };

  /**
   * Handle drawer close.
   */
  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setSelectedUserId(null);
  };

  /**
   * Handle bonus success - refetch user and transactions.
   */
  const handleBonusSuccess = () => {
    refetchUser();
    refetchTransactions();
  };

  /**
   * Transaction table columns.
   */
  const transactionColumns: ColumnsType<TransactionResponse> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 200,
      render: (value: string) => (
        <Text style={{ fontFamily: "monospace", fontSize: "12px" }}>{value}</Text>
      ),
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: (value: number, record: TransactionResponse) => {
        const isPositive = record.transaction_type === "bonus";
        return (
          <Text
            strong
            style={{ color: isPositive ? "#52c41a" : "#ff4d4f" }}
          >
            {isPositive ? "+" : "-"}
            {Math.abs(value)}
          </Text>
        );
      },
    },
    {
      title: "Тип",
      dataIndex: "transaction_type",
      key: "transaction_type",
      width: 120,
      render: (value: string) => {
        const config = {
          bonus: { color: "success", icon: <ArrowUpOutlined />, text: "Начисление" },
          redemption: { color: "error", icon: <ArrowDownOutlined />, text: "Списание" },
        };
        const typeConfig = config[value as keyof typeof config] || {
          color: "default",
          text: value,
        };
        return (
          <Tag icon={typeConfig.icon} color={typeConfig.color}>
            {typeConfig.text}
          </Tag>
        );
      },
    },
    {
      title: "Дата",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (value: string) => {
        const date = dayjs(value);
        return (
          <Tooltip title={date.format("DD.MM.YYYY HH:mm:ss")}>
            <Text>{date.format("DD.MM.YYYY HH:mm")}</Text>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <List title="Пользователи">
        <Table
          columns={columns}
          rowKey="telegram_id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
          scroll={{ x: 900 }}
          locale={{
            emptyText: "Пользователи не найдены",
            triggerDesc: "Нажмите для сортировки по убыванию",
            triggerAsc: "Нажмите для сортировки по возрастанию",
            cancelSort: "Нажмите для отмены сортировки",
          }}
          {...tableProps}
        />
      </List>

      {/* User Detail Drawer */}
      <Drawer
        title="Информация о пользователе"
        placement="right"
        width={720}
        onClose={handleDrawerClose}
        open={isDrawerVisible}
        data-testid="user-detail-panel"
      >
        {userLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : selectedUser ? (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {/* Balance Card */}
            <Card>
              <Statistic
                title="Баланс"
                value={selectedUser.balance}
                valueStyle={{
                  color: selectedUser.balance > 0 ? "#3f8600" : undefined,
                }}
              />
            </Card>

            {/* Add Bonus Button */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsBonusModalVisible(true)}
              data-testid="add-bonus-button"
              block
            >
              Начислить бонусы
            </Button>

            {/* Tabs for Overview and Transactions */}
            <Tabs defaultActiveKey="overview">
              <TabPane tab="Обзор" key="overview">
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Telegram ID">
                    <Text strong>{selectedUser.telegram_id}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Username">
                    {selectedUser.username ? (
                      <Text>@{selectedUser.username}</Text>
                    ) : (
                      <Text type="secondary">—</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Согласие на обработку данных">
                    {selectedUser.consent_personal_data ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Да
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        Нет
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Согласие на маркетинг">
                    {selectedUser.consent_marketing ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Да
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        Нет
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Дата регистрации">
                    {dayjs(selectedUser.created_at).format("DD.MM.YYYY HH:mm")}
                  </Descriptions.Item>
                  {selectedUser.interests && selectedUser.interests.length > 0 && (
                    <Descriptions.Item label="Интересы">
                      <Space wrap>
                        {selectedUser.interests.map((interest, index) => (
                          <Tag key={index} color="blue">
                            {interest}
                          </Tag>
                        ))}
                      </Space>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </TabPane>

              <TabPane tab="Транзакции" key="transactions">
                {transactionsLoading ? (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                  </div>
                ) : transactionsData && transactionsData.length > 0 ? (
                  <Table
                    dataSource={transactionsData}
                    columns={transactionColumns}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 600 }}
                  />
                ) : (
                  <Empty description="Нет транзакций" />
                )}
              </TabPane>
            </Tabs>
          </Space>
        ) : (
          <Empty description="Пользователь не найден" />
        )}
      </Drawer>

      {/* Add Bonus Modal */}
      {selectedUser && (
        <AddBonusModal
          userId={selectedUser.telegram_id}
          visible={isBonusModalVisible}
          onClose={() => setIsBonusModalVisible(false)}
          onSuccess={handleBonusSuccess}
        />
      )}
    </>
  );
};

export default UserList;
